import { DesktopDatePicker } from "@mui/lab";
import EventIcon from "@mui/icons-material/Event";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { deleteKyb, updateKyb } from "api/services/clients/client-kyb";
import LoadingButton from "components/LoadingButton";
import UploadImage from "components/UploadImage";
import { snack } from "components/toast";
import { useConfirm } from "context/ConfirmDialog";
import { Permissions } from "data/permissons";
import { documentTypes } from "data/constants";
import useFileUpload from "hooks/useFileUpload";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { handleError } from "utils/handleError";
import ValidateAccess from "components/ValidateAccess";
import { mulitipleClient, primaryclient } from "assets";
import VirtualizedListboxComponent from "views/billing/invoices/AddInvoice/VirtualizedList";
import { ResType } from "types";
import { getTaskCreateClientDropDown } from "api/services/clients/clients";
import { usePermissions } from "context/PermissionsProvider";


const getDocLabel = (value: string) => {
  const found = documentTypes.find((d) => d.value === value);
  return found ? found.label : value?.replace(/_/g, " ");
};

const diffDays = (expiryDate: string) =>
  moment(expiryDate).startOf("day").diff(moment().startOf("day"), "days");

const getDaysLabel = (expiryDate: string) => {
  const days = diffDays(expiryDate);
  if (days < 0) return `Expired ${Math.abs(days)} days ago`;
  if (days === 0) return "Expires today";
  return `Expires in ${days} days`;
};

const getStatusColor = (expiryDate: string) => {
  if (!expiryDate) return "#9E9E9E";
  const days = diffDays(expiryDate);
  if (days < 0) return "#9E9E9E";
  if (days <= 7) return "#D32F2F";
  if (days <= 30) return "#F57C00";
  return "#388E3C";
};

const activityLog = (data: any) => {
  const entries: { label: string; time: string; color: string }[] = [];
  const days = data?.expiryDate ? diffDays(data.expiryDate) : null;

  if (days !== null && days < 0) {
    entries.push({ label: "Expiry alert sent via email", time: `${moment(data.expiryDate).fromNow()} · System`, color: "#D32F2F" });
  }
  if (days !== null && days <= 7 && days >= 0) {
    entries.push({ label: "7-day reminder triggered", time: `${moment(data.expiryDate).subtract(7, "days").format("DD MMM YYYY")} · System`, color: "#F57C00" });
  }
  if (data?.updatedAt && data.updatedAt !== data.createdAt) {
    entries.push({ label: "Document updated", time: `${moment(data.updatedAt).format("DD MMM YYYY")} · Admin`, color: "#388E3C" });
  }
  entries.push({ label: "Document added to KYB", time: `${moment(data?.createdAt).format("DD MMM YYYY")} · Admin`, color: "#1976D2" });
  return entries;
};

interface Props {
  open: boolean;
  onClose: () => void;
  data: any;
}


function KybExpiryView({ open, onClose, data }: Props) {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { handleFileUpload, deleteFile, deleteAwsFiles, uploading } = useFileUpload();
  const fileDataRef = useRef<any>("");
  const [clientsList, setClientsList] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const { permissions } = usePermissions();
  const [state, setState] = useState<any>({});
  const selectedClient = state.client || state.clientGroup;
  const [newExpiryDate, setNewExpiryDate] = useState<Date | null>(null);
  const initialRef = useRef<any>(null);
  const { isLoading: clientsLoading, isFetching }: ResType = useQuery(
    ["clients", { search: debouncedSearch, page, status: ["ACTIVE", "INACTIVE"], limit: 10 }],
    getTaskCreateClientDropDown,
    {
      enabled:
        open &&
        (permissions.includes(Permissions.VIEW_ALL_CLIENT_MANAGERS) ||
          permissions.includes(Permissions.VIEW_ASSIGNED_CLIENT_MANAGERS)),
      keepPreviousData: true,
      onSuccess: (data: any) => {
        const newClients = data?.data?.result || [];
        const isNewSearch = page === 0;
        const unique = Array.from(
          new Map(
            [...(!isNewSearch ? clientsList : []), ...newClients].map((item) => [item.id, item])
          ).values()
        );
        setClientsList(unique);
        setTotalCount(data?.data?.totalCount ?? null);
      },
    }
  );

  useEffect(() => {
    if (open && data) {
      let docName = data.documentName;
      let customName = "";
      if (!documentTypes.find((d) => d.value === docName)) {
        customName = docName;
        docName = "custom";
      }
      const initial = {
        client: data?.client || null,
        clientGroup: data?.clientGroup || null,
        documentName: docName,
        customName,
        documentNumber: data.documentNumber || "",
        isExpiry: !!data.isExpiry,
        storage: null,
        attachment: data?.storage?.name || "",
        expiryDate: data?.expiryDate || "",
      };
      initialRef.current = initial;
      setState({ ...data, ...initial, attachement: data?.storage?.name || "" });
      setNewExpiryDate(data?.expiryDate ? new Date(data.expiryDate) : null);
    }
  }, [open, data]);

  const isDirty = useMemo(() => {
    const init = initialRef.current;
    if (!init) return false;
    const newDateStr = newExpiryDate ? moment(newExpiryDate).format("YYYY-MM-DD") : "";
    return (
      state.documentName !== init.documentName ||
      state.customName !== init.customName ||
      state.documentNumber !== init.documentNumber ||
      !!state.isExpiry !== init.isExpiry ||
      newDateStr !== init.expiryDate ||
      state.storage instanceof FormData ||
      state.attachement !== init.attachment
    );
  }, [state, newExpiryDate]);

  const { mutate: deleteRecord, isLoading: deleting } = useMutation(deleteKyb, {
    onSuccess: () => {
      snack.success("Document deleted");
      queryClient.invalidateQueries("kyb-org-summary");
      onClose();
    },
    onError: (err: any) => {
      snack.error(handleError(err));
    },
  });
  const handleDeleteAttachment = () => {
    confirm({
      msg: "Are you sure you want to remove this attachment?",
      action: () => {
        setState({
          ...state,
          attachement: "",
          storage: null,
        });

        // Optional: clear existing storage reference so update API removes it
        data.storage = null;
      },
    });
  };

  const handleDelete = () => {
    confirm({
      msg: "Are you sure you want to delete this document? This action cannot be undone.",
      action: () => deleteRecord(data?.id),
    });
  };

  const { mutate: saveAll, isLoading: savingAll } = useMutation(updateKyb, {
    onSuccess: () => {
      snack.success("Document updated successfully");
      queryClient.invalidateQueries("kyb-org-summary");
      onClose();
    },
    onError: (err: any) => {
      snack.error(handleError(err));
      if (fileDataRef.current?.fileId) deleteFile(fileDataRef.current.fileId);
      else if (fileDataRef.current?.file) deleteAwsFiles(fileDataRef.current.file);
    },
  });

  const handleSaveAll = async () => {
    const apiData: any = { ...state };
    apiData.documentName = apiData.documentName === "custom" ? apiData.customName : apiData.documentName;
    apiData.expiryDate = newExpiryDate ? moment(newExpiryDate).format("YYYY-MM-DD") : apiData.expiryDate;

    if (!state.attachement) {
      apiData.storage = null;
    }

    if (state.storage instanceof FormData) {
      const filedata = await handleFileUpload(state.storage);
      apiData.storage = { ...filedata, type: data?.clientGroup ? "clientGroup" : data?.client ? "client" : "organization" };
      fileDataRef.current = filedata;
    }

    saveAll({
      ...apiData,
      client: data?.client?.id || null,
      clientGroup: data?.clientGroup?.id || null,
    });
  };
  const handleClientChange = (_: any, option: any) => {
    if (!option) {
      setState((prev: any) => ({
        ...prev,
        client: null,
        clientGroup: null,
      }));
    } else if (option.type) {
      setState((prev: any) => ({
        ...prev,
        client: null,
        clientGroup: option,
      }));
    } else {
      setState((prev: any) => ({
        ...prev,
        client: option,
        clientGroup: null,
      }));
    }
  };


  const entity = data?.client || data?.clientGroup;
  const entityName = entity?.displayName || "—";
  const entityId = entity?.clientId || entity?.clientGroupId || "";
  const days = data?.expiryDate ? diffDays(data.expiryDate) : null;
  const isExpired = days !== null && days < 0;
  const statusColor = data?.expiryDate ? getStatusColor(data.expiryDate) : "#9E9E9E";
  const logs = activityLog(data);
  const listboxRef = useRef<HTMLUListElement | null>(null);
  const handleScroll = (event: React.SyntheticEvent) => {
        if (selectedClient) return;
        const node = event.currentTarget as HTMLUListElement;
        listboxRef.current = node;
        if (node.scrollTop + node.clientHeight >= node.scrollHeight - 10 && !isFetching) {
            setPage((prev) => prev + 1);
        }
    };
  if (!data) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 2, overflow: "hidden", display: "flex", flexDirection: "column", maxHeight: "90vh" } }}>
      {/* Header */}
      <Box sx={{ bgcolor: "#1A2340", px: 3, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography variant="h6" fontWeight={700} color="white" lineHeight={1.3}>
            {getDocLabel(data?.documentName)}
          </Typography>
          <Typography variant="caption" color="rgba(255,255,255,0.6)">
            {entityName} &nbsp;·&nbsp; {entityId}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          {data?.isExpiry && data?.expiryDate && (
            <Chip
              label={getDaysLabel(data.expiryDate)}
              size="small"
              sx={{ bgcolor: isExpired ? "rgba(255,255,255,0.15)" : `${statusColor}22`, color: isExpired ? "rgba(255,255,255,0.8)" : statusColor, fontWeight: 600, fontSize: 12 }}
            />
          )}
          <ValidateAccess name={Permissions.DELETE_EXPIRY_MANAGEMENT}>
            <Button
              onClick={handleDelete}
              size="small"
              disabled={deleting}
              startIcon={<DeleteOutlinedIcon fontSize="small" />}
              sx={{ color: "#FF5252", bgcolor: "rgba(255,82,82,0.12)", "&:hover": { bgcolor: "rgba(255,82,82,0.22)" }, textTransform: "none", borderRadius: 1 }}
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </ValidateAccess>
          <Button
            onClick={onClose}
            size="small"
            startIcon={<CloseIcon fontSize="small" />}
            sx={{ color: "white", bgcolor: "rgba(255,255,255,0.12)", "&:hover": { bgcolor: "rgba(255,255,255,0.22)" }, textTransform: "none", borderRadius: 1 }}
          >
            Close
          </Button>
        </Box>
      </Box>

      <DialogContent sx={{ p: 0, bgcolor: "#FFFFFF", flex: 1, overflow: "auto" }}>
        {/* Expired alert */}
        {data?.isExpiry && data?.expiryDate && isExpired && (
          <Box px={3} pt={2}>
            <Alert severity="error" icon={<WarningAmberRoundedIcon />} sx={{ borderRadius: 2 }}>
              <Typography variant="body2" fontWeight={600}>Document has expired</Typography>
              <Typography variant="caption">
                This document expired {Math.abs(days!)} days ago. Please arrange for immediate renewal and update the record.
              </Typography>
            </Alert>
          </Box>
        )}

        <Grid container spacing={0}>
          {/* Left column */}
          <Grid item xs={12} md={6} sx={{ borderRight: "1px solid #E0E0E0" }}>
            <Box p={3}>

              {/* Client */}
              <Box mb={2.5}>
                <Typography variant="body2" fontWeight={500} mb={0.5}>
                  Client / Client Group{" "}
                </Typography>
                <Autocomplete
                  disabled={!!(data?.client?.id || data?.clientGroup?.id)}
                  clearOnEscape
                  clearOnBlur={false}
                  options={clientsList}
                  getOptionLabel={(o) => o?.displayName || ""}
                  isOptionEqualToValue={(o, v) => o?.id === v?.id}
                  loading={clientsLoading}
                  loadingText="Loading..."
                  value={clientsList.find((c) => c.id === selectedClient?.id) || null}
                  onInputChange={(_, val, reason) => handleClientChange(val, reason)}
                  onChange={handleClientChange}
                  ListboxComponent={VirtualizedListboxComponent}
                  ListboxProps={{
                    onScroll: handleScroll,
                    style: { scrollbarWidth: "none", msOverflowStyle: "none" },
                  }}
                  disableListWrap
                  noOptionsText={
                    clientsLoading || isFetching ? <CircularProgress size={24} /> : "No options"
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      placeholder="Search client or group..."
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {clientsLoading || isFetching ? (
                              <CircularProgress color="inherit" size={18} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} style={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography sx={{ display: "flex", alignItems: "center" }}>
                        {option.displayName}
                        {option.isPrimary && (
                          <img src={primaryclient} width="22px" style={{ marginLeft: 8 }} alt="primary" />
                        )}
                        {option.type && (
                          <img src={mulitipleClient} width="22px" style={{ marginLeft: 8 }} alt="group" />
                        )}
                      </Typography>
                    </li>
                  )}
                />
              </Box>

              {/* Document Type */}
              <Box mb={2.5}>
                <Typography variant="body2" fontWeight={500} mb={0.5}>
                  Document Type <Typography component="span" color="error">*</Typography>
                </Typography>
                <TextField
                  select fullWidth size="small"
                  name="documentName"
                  value={state.documentName || ""}
                  onChange={(e) => setState({ ...state, documentName: e.target.value })}
                >
                  {documentTypes.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </TextField>
              </Box>

              {state.documentName === "custom" && (
                <Box mb={2.5}>
                  <Typography variant="body2" fontWeight={500} mb={0.5}>Document Name</Typography>
                  <TextField
                    fullWidth size="small" name="customName"
                    value={state.customName || ""}
                    onChange={(e) => setState({ ...state, customName: e.target.value })}
                  />
                </Box>
              )}

              {/* Document ID */}
              <Box mb={2.5}>
                <Typography variant="body2" fontWeight={500} mb={0.5}>
                  Document ID / Reference Number <Typography component="span" color="error">*</Typography>
                </Typography>
                <TextField
                  fullWidth size="small" name="documentNumber"
                  value={state.documentNumber || ""}
                  onChange={(e) => setState({ ...state, documentNumber: e.target.value })}
                  placeholder="e.g. 11223344556677"
                />
              </Box>

              {/* Attachment */}
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={2} display="flex" alignItems="center" gap={0.5}>
                📎 &nbsp;Attachment
              </Typography>

              {data?.storage?.fileUrl && (
                <Box
                  sx={{ border: "1px solid #E0E0E0", borderRadius: 2, p: 1.5, mb: 2, bgcolor: "white", display: "flex", alignItems: "center", gap: 1.5 }}
                >
                  <Box sx={{ width: 36, height: 36, bgcolor: "#FFEBEE", borderRadius: 1, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Typography variant="caption" fontWeight={700} color="#D32F2F" fontSize={9}>PDF</Typography>
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <Typography variant="body2" fontWeight={600} noWrap>{data.storage.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {data.storage.size ? `${(data.storage.size / (1024 * 1024)).toFixed(1)} MB` : ""} · Uploaded {moment(data.storage.createdAt || data.createdAt).format("DD MMM YYYY")}
                    </Typography>
                  </Box>
                  <Box display="flex" gap={0.5}>
                    <a href={data.storage.fileUrl} target="_blank" rel="noreferrer">
                      <VisibilityOutlinedIcon fontSize="small" sx={{ color: "#666", cursor: "pointer" }} />
                    </a>
                    <ValidateAccess name={Permissions.DELETE_EXPIRY_MANAGEMENT}>
                      <DeleteOutlinedIcon
                        fontSize="small"
                        onClick={handleDeleteAttachment}
                        sx={{
                          color: "#666",
                          cursor: "pointer",
                          "&:hover": {
                            color: "#D32F2F",
                          },
                        }}
                      />
                    </ValidateAccess>
                  </Box>
                </Box>
              )}

              <Box
                sx={{ border: "2px dashed #E0E0E0", borderRadius: 2, p: 2, bgcolor: "white", textAlign: "center" }}
              >
                <UploadImage
                  files={state.attachement}
                  existingFile={state.attachement ?? ""}
                  type={data?.clientGroup ? "clientGroup" : data?.client ? "client" : "organization"}
                  client={entity?.id || undefined}
                  name="image"
                  label="Upload file"
                  onChange={(name: string, fileData: FormData) => {
                    setState({ ...state, attachement: name, storage: fileData });
                  }}
                />
                <Typography variant="caption" color="text.secondary">PDF, JPG, PNG · Max 10 MB</Typography>
              </Box>

            </Box>
          </Grid>

          {/* Right column */}
          <Grid item xs={12} md={6}>
            <Box p={3}>
              {/* Has Expiry Toggle */}
              <Box
                sx={{ border: "1px solid #E0E0E0", borderRadius: 2, p: 2, mb: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between", bgcolor: "white" }}
              >
                <Box display="flex" alignItems="center" gap={1.5}>
                  <EventIcon color="action" fontSize="small" />
                  <Box>
                    <Typography variant="body2" fontWeight={500}>Has Expiry Date?</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Toggle off for documents with no expiry (e.g. Aadhaar, PAN)
                    </Typography>
                  </Box>
                </Box>
                <Switch
                  checked={!!state.isExpiry}
                  onChange={(e) => setState({ ...state, isExpiry: e.target.checked })}
                  color="primary"
                />
              </Box>

              {/* Expiry & Renewal */}
              {state.isExpiry && (
                <>
                  <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={2} display="flex" alignItems="center" gap={0.5}>
                    🗓 &nbsp;Expiry &amp; Renewal
                  </Typography>

                  {data?.expiryDate && (
                    <Box
                      sx={{ border: "2px solid #1A2340", borderRadius: 2, p: 2, mb: 2.5, bgcolor: "white" }}
                    >
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Current Expiry Date Set on record as
                      </Typography>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="h6" fontWeight={700} color="#293765">
                          {moment(data.expiryDate).format("DD MMM YYYY")}
                        </Typography>
                        <Chip
                          label={isExpired ? "Expired" : `${days} days`}
                          size="small"
                          sx={{
                            bgcolor: isExpired ? "#F5F5F5" : `${statusColor}18`,
                            color: isExpired ? "#9E9E9E" : statusColor,
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    </Box>
                  )}

                  <Box mb={1}>
                    <Typography variant="body2" fontWeight={500} mb={0.5}>
                      Extend / Update Expiry Date
                    </Typography>
                    <DesktopDatePicker
                      inputFormat="dd-MM-yyyy"
                      value={newExpiryDate}
                      onChange={(val) => setNewExpiryDate(val)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          size="small"
                          helperText="Update this date after renewal to reflect the new validity period"
                        />
                      )}
                    />
                  </Box>
                </>
              )}


              {/* <Divider sx={{ my: 2.5 }} /> */}

            </Box>
          </Grid>
        </Grid>

      </DialogContent>

      {/* Sticky footer — only when changes are made */}
      {isDirty && (
        <Box
          sx={{
            borderTop: "1px solid #E0E0E0",
            px: 3,
            py: 2,
            bgcolor: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            ⓘ &nbsp;Changes are saved to the client's KYB record
          </Typography>
          <Box display="flex" gap={1}>
            <Button variant="outlined" color="inherit" onClick={onClose} sx={{ textTransform: "none" }}>
              Cancel
            </Button>
            <LoadingButton
              loading={uploading || savingAll}
              disabled={uploading || savingAll}
              onClick={handleSaveAll}
              title={uploading ? "Uploading..." : savingAll ? "Saving..." : "Save Changes"}
              color="secondary"
              loadingColor="white"
            />
          </Box>
        </Box>
      )}
    </Dialog>
  );
}

export default KybExpiryView;
